import React, { useMemo, useRef } from 'react';
import { PageRevealStyles, usePageReveal } from '../components/PageReveal';
import InfoBar from '../components/InfoBar';
import StatCard from '../components/StatCard';
import QotdSuccessDonut from '../components/bonuses/QotdSuccessDonut';
import BonusesPerSessionBar from '../components/bonuses/BonusesPerSessionBar';
import FavoriteSlotsBar from '../components/bonuses/FavoriteSlotsBar';
import TopBonusesBar from '../components/bonuses/TopBonusesBar';
import TableGamesDonut from '../components/bonuses/TableGamesDonut';
import TableGamesCumulativeChart from '../components/bonuses/TableGamesCumulativeChart';
import GetEvenDonut from '../components/bonuses/GetEvenDonut';
import HandpayBarChart from '../components/bonuses/HandpayBarChart';
import {
  useBonusesData,
  formatCurrency,
  formatPercentage,
  formatInteger,
} from '../hooks/useBonusesData';

const buildTopBonuses = (rows) => {
  const normalizeSlot = (value) => (value || 'Unknown').replace(/\s+/g, ' ').trim();
  const maxBySlot = new Map();

  rows.forEach((row) => {
    if (!row.largestBonusAmount || row.largestBonusAmount <= 0) return;
    const label = normalizeSlot(row.largestBonusSlot);
    const current = maxBySlot.get(label) ?? 0;
    if (row.largestBonusAmount > current) {
      maxBySlot.set(label, row.largestBonusAmount);
    }
  });

  return Array.from(maxBySlot, ([label, amount]) => ({ label, amount }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10);
};

const buildFavoriteSlots = (rows) => {
  const counts = new Map();
  rows.forEach((row) => {
    if (!row.slots) return;
    row.slots
      .split(',')
      .map((slot) => slot.trim())
      .filter(Boolean)
      .forEach((slot) => {
        counts.set(slot, (counts.get(slot) || 0) + 1);
      });
  });

  return Array.from(counts, ([slot, count]) => ({ slot, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);
};

const buildTableGameCounts = (rows) => {
  const counts = new Map();
  rows.forEach((row) => {
    if (!row.tableGames) return;
    row.tableGames
      .split(',')
      .map((game) => game.trim())
      .filter(Boolean)
      .forEach((game) => {
        counts.set(game, (counts.get(game) || 0) + 1);
      });
  });

  return Array.from(counts, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
};

const buildTableCumulativeSeries = (rows) => {
  let cumulative = 0;
  return rows
    .filter((row) => typeof row.tableGamesTotal === 'number' && !Number.isNaN(row.tableGamesTotal))
    .map((row) => {
      const delta = row.tableGamesTotal; // per-session delta
      cumulative += delta;               // build cumulative by summing deltas
      return { date: row.date, cumulative, delta };
    });
};

const calculateQotdStats = (rows) => {
  const success = rows.filter((row) => row.qotdSuccess).length;
  const fail = rows.length - success;
  const percent = rows.length ? (success / rows.length) * 100 : 0;
  return { success, fail, percent };
};

const calculateBonusStats = (rows) => {
  if (!rows.length) {
    return { most: 0, fewest: 0, average: 0 };
  }
  const counts = rows.map((row) => row.numBonuses || 0);
  const most = counts.length ? Math.max(...counts) : 0;
  const positiveCounts = counts.filter((value) => value > 0);
  const fewest = positiveCounts.length ? Math.min(...positiveCounts) : 0;
  const average = counts.length ? counts.reduce((sum, value) => sum + value, 0) / counts.length : 0;
  return { most, fewest, average };
};

const calculateBestBonuses = (rows) =>
  rows
    .filter((row) => row.largestBonusAmount > 0)
    .sort((a, b) => b.largestBonusAmount - a.largestBonusAmount)
    .slice(0, 4);

const calculateTableStats = (rows) => {
  if (!rows || !rows.length) {
    return { current: 0, biggestWin: 0, biggestLoss: 0, winPercentage: 0 };
  }

  // Build series (cumulative from deltas) and extract deltas
  const series = buildTableCumulativeSeries(rows);
  if (!series.length) {
    return { current: 0, biggestWin: 0, biggestLoss: 0, winPercentage: 0 };
  }

  // Current Position = latest cumulative
  const current = series.at(-1).cumulative;

  // Per-session deltas for stats
  const deltas = series.map((d) => d.delta);
  const played = deltas.filter((v) => v !== 0); // sessions with actual table play

  // Biggest Win/Loss from deltas
  const biggestWin = played.length ? Math.max(...played) : 0;
  const biggestLoss = played.length ? Math.min(...played) : 0;

  // Win % from non-zero deltas
  const wins = played.filter((v) => v > 0).length;
  const winPercentage = played.length ? (wins / played.length) * 100 : 0;

  console.log('[Table Stats Debug]', {
    deltas,
    played,
    current,
    biggestWin,
    biggestLoss,
    winPercentage,
  });

  return { current, biggestWin, biggestLoss, winPercentage };
};

const sumBlackjackRows = (rows) =>
  rows.reduce(
    (acc, row) => {
      const hands = row?.bjTotalHands ?? 0;
      const has10 = row?.bjDealerHas10 ?? 0;
      return {
        totalHands: acc.totalHands + (Number.isFinite(hands) ? hands : 0),
        dealerHas10: acc.dealerHas10 + (Number.isFinite(has10) ? has10 : 0),
      };
    },
    { totalHands: 0, dealerHas10: 0 }
  );

const resolveBlackjackTotals = (totalsByYear, selectedYear) => {
  if (!totalsByYear || Object.keys(totalsByYear).length === 0) return null;
  if (selectedYear == null) {
    return Object.values(totalsByYear).reduce(
      (acc, totals) => {
        if (!totals) return acc;
        const hands = totals.totalHands ?? 0;
        const has10 = totals.dealerHas10 ?? 0;
        return {
          totalHands: acc.totalHands + (Number.isFinite(hands) ? hands : 0),
          dealerHas10: acc.dealerHas10 + (Number.isFinite(has10) ? has10 : 0),
        };
      },
      { totalHands: 0, dealerHas10: 0 }
    );
  }
  if (Object.prototype.hasOwnProperty.call(totalsByYear, selectedYear)) {
    return totalsByYear[selectedYear];
  }
  return null;
};

const calculateBlackjackStats = (rows, totalsByYear, selectedYear) => {
  // Prefer social CSV totals; fall back to summing per-session rows.
  const totals = resolveBlackjackTotals(totalsByYear, selectedYear) ?? sumBlackjackRows(rows);

  const dealerHas10Pct = totals.totalHands
    ? (totals.dealerHas10 / totals.totalHands) * 100
    : 0;
  const dealerNo10Pct = totals.totalHands ? 100 - dealerHas10Pct : 0;

  return {
    totalHands: totals.totalHands,
    dealerHas10: totals.dealerHas10,
    dealerHas10Pct,
    dealerNo10Pct,
  };
};

const calculateGetEvenStats = (rows) => {
  let successCount = 0;
  let failCount = 0;
  let totalGetEven = 0;

  const parseCurrency = (value) => {
    if (value == null || value === '') return 0;
    const s = String(value).trim();
    if (/^\(.*\)$/.test(s)) {
      const n = parseFloat(s.replace(/[()$,]/g, ''));
      return Number.isNaN(n) ? 0 : -Math.abs(n);
    }
    const n = parseFloat(s.replace(/[$,]/g, ''));
    return Number.isNaN(n) ? 0 : n;
  };

  rows.forEach((d) => {
    const getEvenValue = parseCurrency(d.getEven);
    totalGetEven += getEvenValue;
    if (getEvenValue > 0) successCount++;
    else if (getEvenValue < 0) failCount++;
  });

  const totalSessions = successCount + failCount;
  const percent = totalSessions > 0 ? (successCount / totalSessions) * 100 : 0;
  const position = totalGetEven;

  return { success: successCount, fail: failCount, percent, position };
};


const calculateHandpayStats = (rows) => {
  const total = rows.reduce((sum, row) => sum + (row.handpays || 0), 0);
  const average = rows.length ? total / rows.length : 0;
  return { total, average };
};

function BonusesPage({ isActive, selectedYear }) {
  const rootRef = useRef(null);
  usePageReveal(rootRef, isActive);

  const { loading, error, d3, rows, blackjackTotalsByYear } = useBonusesData();

  const filteredRows = useMemo(() => {
    if (selectedYear == null) return rows;
    return rows.filter((row) => row.date.getFullYear() === selectedYear);
  }, [rows, selectedYear]);

  const qotdStats = useMemo(() => calculateQotdStats(filteredRows), [filteredRows]);
  const bonusStats = useMemo(() => calculateBonusStats(filteredRows), [filteredRows]);
  const topBonuses = useMemo(() => calculateBestBonuses(filteredRows), [filteredRows]);
  const topBonusesChartData = useMemo(() => buildTopBonuses(filteredRows), [filteredRows]);
  const favoriteSlots = useMemo(() => buildFavoriteSlots(filteredRows), [filteredRows]);
  const tableGameCounts = useMemo(() => buildTableGameCounts(filteredRows), [filteredRows]);
  const tableSeries = useMemo(() => buildTableCumulativeSeries(filteredRows), [filteredRows]);
  const tableStats = useMemo(() => calculateTableStats(filteredRows), [filteredRows]);
  const blackjackStats = useMemo(
    () => calculateBlackjackStats(filteredRows, blackjackTotalsByYear, selectedYear),
    [filteredRows, blackjackTotalsByYear, selectedYear]
  );
  const getEvenStats = useMemo(() => calculateGetEvenStats(filteredRows), [filteredRows]);
  const handpayStats = useMemo(() => calculateHandpayStats(filteredRows), [filteredRows]);

  if (!isActive) return null;

  const currentPositionTone = tableStats.current > 0 ? 'positive' : tableStats.current < 0 ? 'negative' : 'neutral';
  const getEvenPositionTone = getEvenStats.position > 0 ? 'positive' : getEvenStats.position < 0 ? 'negative' : 'neutral';

  return (
    <section className="app-page" ref={rootRef}>
      <PageRevealStyles />
        <div className="stats-container-three">
        <div className="stats-card-image">
          <img src="/images/vegasmatt.png" alt="Vegas Matt" />
        </div>
        <div className="stats-card-text">
          <p>
            There's a team of amazing people on the Discord channel — a lot of people are saying they're the best. They track
            even more stats than what you see on YouTube. Join the{' '}
            <a href="https://discord.gg/vegasmatt" target="_blank" rel="noopener noreferrer">
              Discord here
            </a>
            .
          </p>
        </div>
      </div>
      <InfoBar
        type="info"
        title="Bonus & Table Game Data"
        html={`
          <p>
            Bonus and table game stats are aggregated from the same Discord data feeds used across Vegas Matt Stats. Each chart
            updates automatically as new sessions are published by the community team.
          </p>
        `}
      />

      {error ? (
        <p style={{ color: '#df87a8', marginTop: '1rem' }}>Failed to load bonus data. Please try again later.</p>
      ) : null}

      <div className="new-charts-container">
        <QotdSuccessDonut d3={d3} counts={qotdStats} />
        <BonusesPerSessionBar d3={d3} data={filteredRows} />
      </div>

      <div className="stats-container">
        <StatCard
          title="QOTD Success Percentage"
          value={loading ? 'Loading…' : formatPercentage(qotdStats.percent, 1)}
          tone="positive"
        />
        <StatCard
          title="Most Bonuses in a Session"
          value={loading ? 'Loading…' : formatInteger(bonusStats.most)}
          tone="positive"
        />
        <StatCard
          title="Fewest Bonuses in a Session"
          value={loading ? 'Loading…' : formatInteger(bonusStats.fewest)}
          tone="negative"
        />
        <StatCard
          title="Average Bonuses per Session"
          value={loading ? 'Loading…' : bonusStats.average.toFixed(1)}
          tone="positive"
        />
      </div>

      <TopBonusesBar d3={d3} data={topBonusesChartData} />


      <div className="stats-container">
        {Array.from({ length: 4 }).map((_, index) => {
          const bonus = topBonuses[index];
          const title = bonus?.largestBonusSlot || '—';
          const value = loading || !bonus ? 'Loading…' : formatCurrency(bonus.largestBonusAmount);
          const subtitle = loading || !bonus ? '—' : new Date(bonus.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
          return (
            <StatCard
              key={index}
              title={title}
              value={value}
              subtitle={subtitle}
              tone="positive"
            />
          );
        })}
      </div>

      <div className="new-charts-container">
        <TableGamesDonut d3={d3} data={tableGameCounts} />
        <TableGamesCumulativeChart d3={d3} data={tableSeries} />
      </div>

      <div className="stats-container">
        <StatCard
          title="Current Position"
          value={loading ? 'Loading…' : formatCurrency(tableStats.current)}
          tone={currentPositionTone}
        />
        <StatCard
          title="Biggest Win"
          value={loading ? 'Loading…' : formatCurrency(tableStats.biggestWin)}
          tone="positive"
        />
        <StatCard
          title="Biggest Loss"
          value={loading ? 'Loading…' : formatCurrency(tableStats.biggestLoss)}
          tone="negative"
        />
        <StatCard
          title="Win Percentage"
          value={loading ? 'Loading…' : formatPercentage(tableStats.winPercentage, 1)}
          tone="positive"
        />
      </div>

      <div className="stats-container">
        <StatCard
          title="Total BJ Hands"
          value={loading ? 'Loading…' : formatInteger(blackjackStats.totalHands)}
          tone="positive"
        />
        <StatCard
          title="Dealer Has 10 Count"
          value={loading ? 'Loading…' : formatInteger(blackjackStats.dealerHas10)}
          tone="negative"
        />
        <StatCard
          title="Dealer Has 10 %"
          value={loading ? 'Loading…' : formatPercentage(blackjackStats.dealerHas10Pct, 2)}
          tone="negative"
        />
        <StatCard
          title="Dealer Does NOT Have 10 %"
          value={loading ? 'Loading…' : formatPercentage(blackjackStats.dealerNo10Pct, 2)}
          tone="positive"
        />
      </div>

      <div className="new-charts-container">
        <GetEvenDonut d3={d3} data={getEvenStats} />
        <HandpayBarChart d3={d3} data={filteredRows} />
      </div>

      <div className="stats-container">
        <StatCard
          title="Get Even Better/Worse Win %"
          value={loading ? 'Loading…' : formatPercentage(getEvenStats.percent, 1)}
          tone="positive"
        />
        <StatCard
          title="Get Even Better/Worse Position"
          value={loading ? 'Loading…' : formatCurrency(getEvenStats.position, { includeCents: true })}
          tone={getEvenPositionTone}
        />
        <StatCard
          title="Total Handpays"
          value={loading ? 'Loading…' : formatInteger(handpayStats.total)}
          tone="positive"
        />
        <StatCard
          title="Average Handpays per Session"
          value={loading ? 'Loading…' : handpayStats.average.toFixed(2)}
          tone="positive"
        />
      </div>
    </section>
  );
}

export default BonusesPage;
